import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { safeDownloadBlob } from "../lib/downloadUtils";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Plus, Loader2, Upload, Archive, AlertTriangle } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

import {
  EMPTY_CIERRE, EMPTY_PROJECT_FORM, getCorrectedView,
} from "../components/projects/projectUtils";
import { ProjectsSummaryCards } from "../components/projects/ProjectsSummaryCards";
import { RdPipelineCard } from "../components/projects/RdPipelineCard";
import { EconomicVistaCard } from "../components/projects/EconomicVistaCard";
import { CostComparisonCard } from "../components/projects/CostComparisonCard";
import { ProjectsFilters } from "../components/projects/ProjectsFilters";
import { ProjectsTable } from "../components/projects/ProjectsTable";
import { AnnualBreakdownTab } from "../components/projects/AnnualBreakdownTab";
import { AuditPanelTab } from "../components/projects/AuditPanelTab";
import { ImportIncidentsPanel } from "../components/projects/ImportIncidentsPanel";
import { ProjectCreateDialog } from "../components/projects/ProjectCreateDialog";
import { ProjectEditDialog } from "../components/projects/ProjectEditDialog";
import { ProjectCierreDialog } from "../components/projects/ProjectCierreDialog";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProjectsPage() {
  const { token, isAdmin } = useAuth();
  const [balances, setBalances] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [managerFilter, setManagerFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState("2026");
  const [dataView, setDataView] = useState("source");
  const [includeInvestmentAid, setIncludeInvestmentAid] = useState(false);

  // Create dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_PROJECT_FORM);

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [formLoading, setFormLoading] = useState(false);

  // Ficha & cierre
  const [fichaLoading, setFichaLoading] = useState(null);
  const [cierreDialogOpen, setCierreDialogOpen] = useState(false);
  const [cierreProject, setCierreProject] = useState(null);
  const [cierreLoading, setCierreLoading] = useState(false);
  const [cierreForm, setCierreForm] = useState(EMPTY_CIERRE);
  const [newKpi, setNewKpi] = useState({ nombre: "", valor: "" });
  const [newLeccion, setNewLeccion] = useState("");
  const [newPatente, setNewPatente] = useState({ titulo: "", descripcion: "", tipo: "Patente", estado: "En trámite" });
  const [newMiembro, setNewMiembro] = useState("");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [balancesData, summaryData] = await Promise.all([
        api.getRDProjectBalances(token, parseInt(yearFilter), includeInvestmentAid),
        api.getRDBalancesSummary(token, parseInt(yearFilter), includeInvestmentAid),
      ]);
      setBalances(balancesData || []);
      setSummary(summaryData || null);
    } catch (error) {
      toast.error("Error al cargar datos de proyectos I+D");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [token, yearFilter, includeInvestmentAid]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Handlers ───────────────────────────────────────────────────────────

  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setLoading(true);
      await api.importRDBalancesFromExcel(token, file, parseInt(yearFilter));
      toast.success("Datos importados correctamente");
      fetchData();
    } catch (error) {
      toast.error("Error al importar archivo");
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const openNewProject = () => {
    setCreateForm(EMPTY_PROJECT_FORM);
    setCreateDialogOpen(true);
  };

  const openEditDialog = (project) => {
    setSelectedProject(project);
    const subsidy = project.subsidy || 0;
    const financial_savings = project.financial_savings || 0;
    const tax_deduction = project.tax_deduction || 0;
    const expenses = project.expenses || 0;
    const income = subsidy + financial_savings + tax_deduction;
    setEditForm({
      project_name: project.project_name || "",
      project_type: project.project_type || "",
      status: project.status || "",
      expenses, income, balance: income - expenses,
      subsidy, financial_savings, tax_deduction,
      budget: project.budget || 0,
      financing: project.financing || 0,
      notes: project.notes || "",
      year_start: project.year_start || "",
      year_end: project.year_end || "",
      project_manager: project.project_manager || "",
    });
    setEditDialogOpen(true);
  };

  const handleCreateProject = async () => {
    if (!createForm.project_name.trim()) {
      toast.error("El nombre del proyecto es obligatorio");
      return;
    }
    if (!createForm.project_type.trim()) {
      toast.error("El tipo de proyecto es obligatorio");
      return;
    }
    setFormLoading(true);
    try {
      await api.createRDProjectBalance(token, { ...createForm, year: parseInt(yearFilter) });
      toast.success("Proyecto creado correctamente");
      setCreateDialogOpen(false);
      setCreateForm(EMPTY_PROJECT_FORM);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear el proyecto");
    } finally {
      setFormLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedProject) return;
    setFormLoading(true);
    try {
      const res = await api.updateRDProjectBalance(token, selectedProject.id, {
        expenses: editForm.expenses,
        income: editForm.income,
        subsidy: editForm.subsidy,
        financial_savings: editForm.financial_savings,
        tax_deduction: editForm.tax_deduction,
        budget: editForm.budget,
        financing: editForm.financing,
        notes: editForm.notes,
        status: editForm.status,
        year_start: editForm.year_start,
        year_end: editForm.year_end,
        project_manager: editForm.project_manager,
      });
      if (res?.deviation_alert) {
        toast.warning(
          `Aviso enviado: los nuevos valores de "${res.deviation_alert.project_name}" se alejan de la fuente verificada (${res.deviation_alert.deviations.map((d) => d.field).join(", ")})`,
          { duration: 8000 }
        );
      } else {
        toast.success("Proyecto actualizado");
      }
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar");
    } finally {
      setFormLoading(false);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = (projectId) => {
    const project = balances.find((b) => b.id === projectId);
    setDeleteTarget({ id: projectId, name: project?.project_name || "este proyecto" });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.deleteRDProjectBalance(token, deleteTarget.id);
      toast.success("Proyecto eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleExportFicha = async (project, format) => {
    setFichaLoading(`${project.id}-${format}`);
    try {
      const endpoint = format === "comercial"
        ? `${API}/api/rd-projects/balances/${project.id}/ficha-comercial/pdf`
        : `${API}/api/rd-projects/balances/${project.id}/ficha/${format}`;
      const ext = format === "comercial" ? "pdf" : format;
      const prefix = format === "comercial" ? "Comercial_" : "Ficha_";
      await safeDownloadBlob(endpoint, `${prefix}${project.project_name.replace(/\s+/g, "_").slice(0, 30)}.${ext}`, token);
      toast.success(format === "comercial" ? "Ficha comercial descargada" : `Ficha ${format.toUpperCase()} descargada`);
    } catch (e) {
      toast.error(e.message || "Error al generar la ficha");
    } finally {
      setFichaLoading(null);
    }
  };

  const [bulkLoading, setBulkLoading] = useState(false);
  const handleBulkComercialZip = async () => {
    setBulkLoading(true);
    try {
      const url = `${API}/api/rd-projects/balances/export-comercial-zip?year=${parseInt(yearFilter)}&only_active=true`;
      const filename = `Fichas_Comerciales_IDi_${yearFilter}.zip`;
      toast.info("Generando ZIP de fichas comerciales… puede tardar unos segundos");
      await safeDownloadBlob(url, filename, token);
      toast.success("ZIP de fichas comerciales descargado");
    } catch (e) {
      toast.error(e.message || "Error generando el ZIP");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleOpenCierre = async (project) => {
    setCierreProject(project);
    setCierreLoading(true);
    setCierreDialogOpen(true);
    try {
      const res = await fetch(
        `${API}/api/rd-projects/balances/${project.id}/cierre`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      setCierreForm({ ...EMPTY_CIERRE, ...data });
    } catch {
      setCierreForm(EMPTY_CIERRE);
    } finally {
      setCierreLoading(false);
    }
  };

  const handleSaveCierre = async () => {
    setCierreLoading(true);
    try {
      await fetch(
        `${API}/api/rd-projects/balances/${cierreProject.id}/cierre`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify(cierreForm),
        }
      );
      toast.success("Ficha de cierre guardada");
    } catch {
      toast.error("Error guardando el cierre");
    } finally {
      setCierreLoading(false);
    }
  };

  const handleExportCierre = async (format) => {
    try {
      const url = `${API}/api/rd-projects/balances/${cierreProject.id}/cierre/${format}`;
      const filename = `Cierre_${cierreProject.project_name.replace(/\s+/g, "_").slice(0, 30)}.${format}`;
      await safeDownloadBlob(url, filename, token);
      toast.success(`Informe de cierre ${format.toUpperCase()} descargado`);
    } catch (e) {
      toast.error(e.message || "Error al exportar");
    }
  };

  // ── Filter balances ────────────────────────────────────────────────────

  const filteredBalances = balances.filter(p => {
    const q = searchTerm.toLowerCase();
    const matchesSearch = !q
      || p.project_name?.toLowerCase().includes(q)
      || p.notes?.toLowerCase().includes(q)
      || p.project_type?.toLowerCase().includes(q)
      || p.project_manager?.toLowerCase().includes(q);
    const matchesStatus = statusFilter === "all" || p.status === statusFilter;
    const matchesType = typeFilter === "all" || p.project_type === typeFilter;
    const matchesManager = managerFilter === "all" || p.project_manager === managerFilter;
    return matchesSearch && matchesStatus && matchesType && matchesManager;
  });

  const displayBalances = dataView === "corrected"
    ? filteredBalances.map(getCorrectedView)
    : filteredBalances;

  // ── Render ─────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="projects-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Balances Proyectos I+D</h1>
          <p className="text-muted-foreground mt-1">
            {filteredBalances.length} balances económicos de proyectos I+D (previsto vs incurrido vs fondos externos)
          </p>
        </div>
        <div className="flex gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImportExcel}
            accept=".xlsx,.xls"
            className="hidden"
          />
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="import-excel-btn">
            <Upload className="w-4 h-4 mr-2" />Importar Excel
          </Button>
          <Button
            variant="outline"
            onClick={handleBulkComercialZip}
            disabled={bulkLoading}
            data-testid="bulk-comercial-zip-btn"
            title="Descargar ZIP con todas las fichas comerciales activas"
          >
            {bulkLoading
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Archive className="w-4 h-4 mr-2" />
            }
            ZIP comerciales
          </Button>
          <Button onClick={openNewProject} data-testid="new-project-btn">
            <Plus className="w-4 h-4 mr-2" />Nuevo Proyecto
          </Button>
        </div>
      </div>

      <ProjectsSummaryCards summary={summary} />

      <ImportIncidentsPanel token={token} />

      {balances.some((b) => b.balance < 0) && (
        <div
          className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-900 px-4 py-3 text-sm text-red-700 dark:text-red-400"
          data-testid="negative-balance-banner"
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>
            {balances.filter((b) => b.balance < 0).length} proyecto(s) de I+D cerrando en pérdidas:{" "}
            {balances.filter((b) => b.balance < 0).map((b) => b.project_name).join(", ")}
          </span>
        </div>
      )}

      <EconomicVistaCard token={token} isAdmin={isAdmin} />

      <RdPipelineCard token={token} />

      <CostComparisonCard token={token} />

      <Tabs defaultValue="balances" className="space-y-4">
        <TabsList data-testid="projects-view-tabs">
          <TabsTrigger value="balances" data-testid="tab-balances">Balances</TabsTrigger>
          <TabsTrigger value="annual" data-testid="tab-annual">Vista Proyecto-Año</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Auditoría</TabsTrigger>
        </TabsList>

        <TabsContent value="balances" className="space-y-4">
          <ProjectsFilters
            balances={balances}
            searchTerm={searchTerm} setSearchTerm={setSearchTerm}
            statusFilter={statusFilter} setStatusFilter={setStatusFilter}
            typeFilter={typeFilter} setTypeFilter={setTypeFilter}
            managerFilter={managerFilter} setManagerFilter={setManagerFilter}
            yearFilter={yearFilter} setYearFilter={setYearFilter}
            dataView={dataView} setDataView={setDataView}
            includeInvestmentAid={includeInvestmentAid} setIncludeInvestmentAid={setIncludeInvestmentAid}
          />

          <ProjectsTable
            filteredBalances={displayBalances}
            searchTerm={searchTerm}
            statusFilter={statusFilter}
            onNewProject={openNewProject}
            onImportClick={() => fileInputRef.current?.click()}
            onEdit={openEditDialog}
            onDelete={handleDelete}
            onExportFicha={handleExportFicha}
            onOpenCierre={handleOpenCierre}
            fichaLoading={fichaLoading}
            token={token}
          />
        </TabsContent>

        <TabsContent value="annual">
          <AnnualBreakdownTab token={token} balances={balances} />
        </TabsContent>

        <TabsContent value="audit">
          <AuditPanelTab token={token} balances={balances} />
        </TabsContent>
      </Tabs>

      <ProjectCreateDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        form={createForm}
        setForm={setCreateForm}
        balances={balances}
        yearFilter={yearFilter}
        onSubmit={handleCreateProject}
        loading={formLoading}
      />

      <ProjectEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        selectedProject={selectedProject}
        form={editForm}
        setForm={setEditForm}
        balances={balances}
        onSave={handleSaveEdit}
        loading={formLoading}
      />

      <ProjectCierreDialog
        open={cierreDialogOpen}
        onOpenChange={setCierreDialogOpen}
        project={cierreProject}
        form={cierreForm}
        setForm={setCierreForm}
        newKpi={newKpi} setNewKpi={setNewKpi}
        newLeccion={newLeccion} setNewLeccion={setNewLeccion}
        newPatente={newPatente} setNewPatente={setNewPatente}
        newMiembro={newMiembro} setNewMiembro={setNewMiembro}
        onSave={handleSaveCierre}
        onExport={handleExportCierre}
        loading={cierreLoading}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Eliminar proyecto"
        description={`¿Estás seguro de que quieres eliminar "${deleteTarget?.name}"? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        onConfirm={confirmDelete}
        loading={deleting}
        testId="delete-project-confirm-dialog"
      />
    </div>
  );
}
